import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import { categoriesFor } from '../../lib/clubLedger';
import { todayIso, formatDate } from '../../lib/clubOps';
import { uploadImageFile } from '../../lib/pasteImageUpload';
import LazyImage from '../gui/lazyImage';

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PRESET_KEYS = ['type', 'date', 'amount', 'category', 'description', 'paidBy', 'paidByUser', 'paidByName', 'images'];

/** Only the entry fields of a preset (e.g. built from a support ticket), never UI state. */
function presetFields(preset) {
    const fields = {};
    if (!preset || typeof preset !== 'object') {
        return fields;
    }
    PRESET_KEYS.forEach((key) => {
        if (preset[key] !== undefined && preset[key] !== null) {
            fields[key] = key === 'images' ? [...(Array.isArray(preset.images) ? preset.images : [])] : preset[key];
        }
    });
    return fields;
}

function initialState({
    isNew, transaction, presetType, preset,
}) {
    if (isNew || !transaction) {
        return {
            type: presetType === 'income' ? 'income' : 'expense',
            date: todayIso(),
            amount: '',
            category: '',
            description: '',
            paidBy: 'club',
            paidByUser: 0,
            paidByName: '',
            images: [],
            ...presetFields(preset),
            memberSearch: '',
            showMembers: false,
            uploading: false,
            confirmDelete: false,
            error: '',
            isSaving: false,
            emailTo: '',
            emailCc: '',
            emailMessage: '',
            emailSending: false,
            emailLog: [],
            emailPreview: null,
            payerBankAccount: '',
            bankSaving: false,
        };
    }
    return {
        type: transaction.type || 'expense',
        date: transaction.date ? String(transaction.date).slice(0, 10) : todayIso(),
        amount: transaction.amount != null ? String(transaction.amount) : '',
        category: transaction.category || '',
        description: transaction.description || '',
        paidBy: transaction.paidBy || 'club',
        paidByUser: transaction.paidByUser || 0,
        paidByName: transaction.paidByName || '',
        images: Array.isArray(transaction.images) ? [...transaction.images] : [],
        memberSearch: '',
        showMembers: false,
        uploading: false,
        confirmDelete: false,
        error: '',
        isSaving: false,
        emailTo: '',
        emailCc: '',
        emailMessage: '',
        emailSending: false,
        emailLog: Array.isArray(transaction.accountantEmails) ? [...transaction.accountantEmails] : [],
        emailPreview: null,
        payerBankAccount: '',
        bankSaving: false,
    };
}

function thumb(img = {}) {
    return img.s3SmallLink || img.s3MediumLink || img.s3Link || img.url || img.src || '';
}

function fullSize(img = {}) {
    return img.s3Link || img.s3MediumLink || img.url || img.src || img.s3SmallLink || '';
}

@observer
class ClubOpsLedgerDrawer extends Component {
    constructor(props) {
        super(props);
        const { appState, userStore } = props.stores || {};
        const accountantEmail = appState?.currentCustomer?.featureFlags?.accountantEmail
            || userStore?.customer?.featureFlags?.accountantEmail || '';
        this.state = { ...initialState(props), emailTo: accountantEmail };
    }

    componentDidMount() {
        this.loadPreview();
    }

    componentWillUnmount() {
        if (this.previewTimer) {
            clearTimeout(this.previewTimer);
        }
    }

    /** Re-render the mail preview 500 ms after the message stopped changing. */
    schedulePreview = () => {
        if (this.previewTimer) {
            clearTimeout(this.previewTimer);
        }
        this.previewTimer = setTimeout(this.loadPreview, 500);
    }

    loadPreview = async () => {
        const { isNew, transaction } = this.props;
        const { clubTransactionStore } = this.props.stores;
        if (isNew || !transaction?.id || transaction.type !== 'expense' || !clubTransactionStore?.emailPreview) {
            return;
        }
        const preview = await clubTransactionStore.emailPreview(transaction.id, this.state.emailMessage);
        if (!preview) {
            return;
        }
        this.setState(prev => ({
            emailPreview: preview,
            // The club's remembered address wins over an empty field, never over what the officer typed.
            emailTo: prev.emailTo || preview.suggestedTo || '',
        }));
    }

    saveBankAccount = async () => {
        const { clubTransactionStore } = this.props.stores;
        const { transaction, callback = () => {} } = this.props;
        const digits = String(this.state.payerBankAccount || '').replace(/\D/g, '');
        if (digits.length !== 11 || !transaction?.id) {
            return;
        }
        this.setState({ bankSaving: true, error: '' });
        const response = await clubTransactionStore.setPayerBankAccount(transaction.id, digits);
        if (response?.status === 202) {
            this.setState({ bankSaving: false, payerBankAccount: '' }, this.loadPreview);
            callback();
            return;
        }
        this.setState({ bankSaving: false, error: response?.message || clubTransactionStore.lastError || 'Could not save' });
    }

    onEmailMessage = (e) => {
        this.setState({ emailMessage: e.target.value, error: '' }, this.schedulePreview);
    }

    openReceipt = (img) => {
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        appState.openDrawer('imageFullscreen', {
            src: fullSize(img),
            height: appState.drawerHeightLarge,
            showCloseButton: true,
        }, drawerLevel + 1);
    }

    canEmail = () => {
        const {
            emailTo, emailCc, emailSending, type,
        } = this.state;
        const { isNew, transaction } = this.props;
        const ccOk = String(emailCc || '').split(/[\s,;]+/).filter(Boolean).every(a => EMAIL_RX.test(a));
        return !isNew && !!transaction?.id && type === 'expense' && !emailSending
            && EMAIL_RX.test(emailTo.trim()) && ccOk;
    }

    sendEmail = async () => {
        if (!this.canEmail()) {
            return;
        }
        const { clubTransactionStore } = this.props.stores;
        const { transaction, callback = () => {} } = this.props;
        const { emailTo, emailCc, emailMessage } = this.state;
        this.setState({ emailSending: true, error: '' });
        const response = await clubTransactionStore.emailToAccountant(transaction.id, {
            to: emailTo, cc: emailCc, message: emailMessage,
        });
        if (response?.status === 202) {
            const log = Array.isArray(response.data?.accountantEmails) ? response.data.accountantEmails : [];
            this.setState({ emailSending: false, emailMessage: '', emailLog: log }, this.loadPreview);
            callback();
            return;
        }
        this.setState({ emailSending: false, error: response?.message || clubTransactionStore.lastError || 'Could not send' });
    }

    onField = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value, error: '' });
    }

    onType = (e) => {
        const type = e.target.value;
        this.setState({
            type, category: '', paidBy: 'club', paidByUser: 0, paidByName: '',
        });
    }

    onPaidBy = (e) => {
        const paidBy = e.target.value;
        this.setState(paidBy === 'club' ? { paidBy, paidByUser: 0, paidByName: '' } : { paidBy });
    }

    onMemberSearch = (e) => {
        const { ammoSaleStore } = this.props.stores;
        const { value } = e.target;
        this.setState({ memberSearch: value, showMembers: true });
        ammoSaleStore.searchMembers(value);
    }

    pickMember = (member) => {
        this.setState({
            paidByUser: member.id,
            paidByName: `${member.firstname || ''} ${member.lastname || ''}`.trim(),
            memberSearch: '',
            showMembers: false,
        });
    }

    onFile = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) {
            return;
        }
        this.setState({ uploading: true, error: '' });
        for (let i = 0; i < files.length; i += 1) {
            try {
                // eslint-disable-next-line no-await-in-loop
                const uploaded = await uploadImageFile(files[i]);
                this.setState(prev => ({ images: [...prev.images, uploaded].slice(0, 10) }));
            } catch (err) {
                this.setState({ uploading: false, error: err.message || 'Upload failed' });
                return;
            }
        }
        this.setState({ uploading: false });
    }

    removeImage = (index) => {
        this.setState(prev => ({ images: prev.images.filter((unused, i) => i !== index) }));
    }

    canSave = () => {
        const {
            isSaving, amount, category, type, paidBy, paidByUser,
        } = this.state;
        return !isSaving && parseFloat(String(amount).replace(',', '.')) > 0 && !!category
            && (type === 'income' || paidBy === 'club' || paidByUser > 0);
    }

    save = async () => {
        const { appState, clubTransactionStore } = this.props.stores;
        const {
            isNew, transaction, callback = () => {}, drawerLevel = 1,
        } = this.props;
        if (!this.canSave()) {
            return;
        }
        const {
            type, date, amount, category, description, paidBy, paidByUser, images,
        } = this.state;
        this.setState({ isSaving: true, error: '' });
        const input = {
            type, date, amount, category, description, paidBy, paidByUser, images, customer: appState.currentCustomer?.id,
        };
        const response = isNew
            ? await clubTransactionStore.add(input)
            : await clubTransactionStore.update(transaction.id, input);
        if (response?.status === 201 || response?.status === 202) {
            callback(response.data || {});
            appState.toggleDrawer(false, drawerLevel);
            return;
        }
        this.setState({ isSaving: false, error: response?.message || clubTransactionStore.lastError || 'Could not save' });
    }

    remove = async () => {
        const { appState, clubTransactionStore } = this.props.stores;
        const { transaction, callback = () => {}, drawerLevel = 1 } = this.props;
        const response = await clubTransactionStore.remove(transaction.id);
        if (response?.status === 202) {
            callback();
            appState.toggleDrawer(false, drawerLevel);
            return;
        }
        this.setState({ confirmDelete: false, error: response?.message || 'Could not delete' });
    }

    render() {
        const { isNew, transaction = {} } = this.props;
        const { ammoSaleStore } = this.props.stores;
        const { members = [] } = ammoSaleStore;
        const {
            type, date, amount, category, description, paidBy, paidByName, images,
            memberSearch, showMembers, uploading, confirmDelete, error, isSaving,
            emailTo, emailCc, emailMessage, emailSending, emailLog, emailPreview, payerBankAccount, bankSaving,
        } = this.state;
        const bankDigits = String(payerBankAccount || '').replace(/\D/g, '');
        const categories = categoriesFor(type);
        const canSave = this.canSave();
        const canEmail = this.canEmail();

        return (
            <div class='w-100 px-3 py-3'>
                <h5 class='mb-3'>
                    <i class='fa-solid fa-coins me-2' />
                    {isNew
                        ? <Text id='clubOps.ledger-new'>Register entry</Text>
                        : <Text id='clubOps.ledger-edit'>Edit entry</Text>}
                </h5>
                {!!transaction.source && (
                    <div class='alert alert-info py-2'>
                        <i class='fa-solid fa-boxes-stacked me-2' />
                        <Text id='clubOps.ledger-from-ammo-help'>From ammo purchases. You can adjust the amount, date and category.</Text>
                    </div>
                )}

                <div class='mb-3 d-flex gap-3'>
                    {['expense', 'income'].map(t => (
                        <div key={t} class='form-check'>
                            <input class='form-check-input' type='radio' name='type' id={`ledgerType-${t}`} value={t} checked={type === t} onChange={this.onType} />
                            <label class='form-check-label' for={`ledgerType-${t}`}>
                                {t === 'expense'
                                    ? <Text id='clubOps.ledger-type-expense'>Expense</Text>
                                    : <Text id='clubOps.ledger-type-income'>Income</Text>}
                            </label>
                        </div>
                    ))}
                </div>

                <div class='row g-2 mb-3'>
                    <div class='col-6'>
                        <label class='form-label'><Text id='clubOps.ledger-col-date'>Date</Text></label>
                        <input class='form-control' type='date' name='date' value={date} onInput={this.onField} />
                    </div>
                    <div class='col-6'>
                        <label class='form-label'><Text id='clubOps.ledger-col-amount'>Amount</Text> (kr)</label>
                        <input class='form-control' type='number' inputMode='decimal' min='0' step='0.01' name='amount' value={amount} onInput={this.onField} />
                    </div>
                    <div class='col-12'>
                        <label class='form-label'><Text id='clubOps.ledger-col-category'>Category</Text></label>
                        <select class='form-select' name='category' value={category} onChange={this.onField}>
                            <option value=''>–</option>
                            {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                        </select>
                    </div>
                    <div class='col-12'>
                        <label class='form-label'><Text id='clubOps.ledger-col-description'>Description</Text></label>
                        <input class='form-control' type='text' name='description' maxLength='300' value={description} onInput={this.onField} />
                    </div>
                </div>

                {type === 'expense' && (
                    <div class='mb-3'>
                        <div class='form-label'><Text id='clubOps.ledger-col-paid-by'>Paid by</Text></div>
                        <div class='d-flex gap-3 mb-2'>
                            {['club', 'member'].map(p => (
                                <div key={p} class='form-check'>
                                    <input class='form-check-input' type='radio' name='paidBy' id={`ledgerPaidBy-${p}`} value={p} checked={paidBy === p} onChange={this.onPaidBy} />
                                    <label class='form-check-label' for={`ledgerPaidBy-${p}`}>
                                        {p === 'club'
                                            ? <Text id='clubOps.ledger-paid-by-club'>Club</Text>
                                            : <Text id='clubOps.ledger-paid-by-member'>A member (to be reimbursed)</Text>}
                                    </label>
                                </div>
                            ))}
                        </div>
                        {paidBy === 'member' && (
                            <div class='position-relative'>
                                {paidByName
                                    ? (
                                        <div class='mb-2'>
                                            <span class='badge bg-primary fs-6'>{paidByName}</span>
                                            <button type='button' class='btn btn-link btn-sm' onClick={() => this.setState({ paidByUser: 0, paidByName: '' })}>
                                                <Text id='clubOps.ledger-change-member'>Change</Text>
                                            </button>
                                        </div>
                                    )
                                    : (
                                        <input
                                            class='form-control'
                                            type='text'
                                            name='memberSearch'
                                            placeholder='Søk medlem…'
                                            value={memberSearch}
                                            onInput={this.onMemberSearch}
                                        />
                                    )}
                                {showMembers && memberSearch.length >= 2 && members.length > 0 && (
                                    <div class='list-group position-absolute w-100 shadow' style='z-index: 1050;'>
                                        {members.map(m => (
                                            <button
                                                key={m.id}
                                                type='button'
                                                class='list-group-item list-group-item-action'
                                                data-member={m.id}
                                                onClick={() => this.pickMember(m)}
                                            >
                                                {`${m.firstname || ''} ${m.lastname || ''}`.trim()}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                <div class='mb-3'>
                    <label class='form-label'><Text id='clubOps.ledger-receipts'>Receipts</Text></label>
                    <div class='d-flex flex-wrap gap-2 mb-2'>
                        {images.map((img, i) => (
                            <div key={thumb(img) || i} class='position-relative'>
                                <div
                                    class='ledger-receipt rounded overflow-hidden cursor-pointer'
                                    style='width: 72px; height: 72px;'
                                    data-receipt={i}
                                    onClick={() => this.openReceipt(img)}
                                >
                                    <LazyImage
                                        src={thumb(img)}
                                        lowResBase64={img.lowResBase64}
                                        alt=''
                                        style='width: 72px; height: 72px; object-fit: cover;'
                                    />
                                </div>
                                <button
                                    type='button'
                                    class='btn btn-sm btn-danger rounded-circle position-absolute top-0 end-0'
                                    style='transform: translate(30%, -30%);'
                                    onClick={() => this.removeImage(i)}
                                >
                                    <i class='fa-solid fa-xmark' />
                                </button>
                            </div>
                        ))}
                    </div>
                    <input class='form-control' type='file' accept='image/*' multiple disabled={uploading} onChange={this.onFile} />
                    {uploading && <small class='text-body-secondary'><i class='fa-solid fa-spinner fa-spin me-1' /><Text id='clubOps.ledger-uploading'>Uploading…</Text></small>}
                </div>

                {!isNew && type === 'expense' && (
                    <div class='mb-3 border rounded p-3 bg-body-secondary'>
                        <h6 class='mb-1'>
                            <i class='fa-solid fa-envelope me-2' />
                            <Text id='clubOps.ledger-email-title'>Send to accountant</Text>
                        </h6>
                        <div class='small text-body-secondary mb-2'>
                            <Text id='clubOps.ledger-email-help'>
                                Sends the expense with the receipts attached. Paying the member is marked manually afterwards.
                            </Text>
                        </div>
                        <div class='row g-2'>
                            <div class='col-12 col-md-6'>
                                <label class='form-label mb-1' for='ledgerEmailTo'>
                                    <Text id='clubOps.ledger-email-to'>To e-mail</Text>
                                </label>
                                <input
                                    class='form-control'
                                    type='email'
                                    id='ledgerEmailTo'
                                    name='emailTo'
                                    value={emailTo}
                                    onInput={this.onField}
                                />
                            </div>
                            <div class='col-12 col-md-6'>
                                <label class='form-label mb-1' for='ledgerEmailCc'>
                                    <Text id='clubOps.ledger-email-cc'>Copy to (cc)</Text>
                                </label>
                                <input
                                    class='form-control'
                                    type='text'
                                    id='ledgerEmailCc'
                                    name='emailCc'
                                    placeholder='kasserer@klubb.no, leder@klubb.no'
                                    value={emailCc}
                                    onInput={this.onField}
                                />
                            </div>
                            <div class='col-12'>
                                <label class='form-label mb-1' for='ledgerEmailMessage'>
                                    <Text id='clubOps.ledger-email-message'>Message (optional)</Text>
                                </label>
                                <textarea
                                    class='form-control'
                                    id='ledgerEmailMessage'
                                    name='emailMessage'
                                    rows='4'
                                    maxLength='2000'
                                    value={emailMessage}
                                    onInput={this.onEmailMessage}
                                />
                            </div>
                        </div>
                        {!!emailPreview && (
                            <div class='mt-3 ledger-email-preview'>
                                <div class='small text-body-secondary mb-1'>
                                    <Text id='clubOps.ledger-email-preview'>Preview of the e-mail</Text>
                                </div>
                                {emailPreview.bankAccountMissing && (
                                    <div class='alert alert-warning py-2 mb-2'>
                                        <div class='small mb-2'>
                                            <i class='fa-solid fa-triangle-exclamation me-1' />
                                            <Text id='clubOps.ledger-email-no-bank'>
                                                The member has no bank account on their profile. Enter it here or ask them to add it under My page.
                                            </Text>
                                        </div>
                                        <div class='d-flex gap-2'>
                                            <input
                                                class='form-control form-control-sm'
                                                type='text'
                                                inputMode='numeric'
                                                name='payerBankAccount'
                                                placeholder='1234.56.78901'
                                                value={payerBankAccount}
                                                onInput={this.onField}
                                            />
                                            <button
                                                type='button'
                                                class='btn btn-sm btn-warning rounded-pill text-nowrap'
                                                data-action='save-bank'
                                                disabled={bankSaving || bankDigits.length !== 11}
                                                onClick={this.saveBankAccount}
                                            >
                                                {bankSaving && <i class='fa-solid fa-spinner fa-spin me-1' />}
                                                <Text id='clubOps.ledger-email-save-bank'>Save account number</Text>
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <div class='border rounded overflow-hidden'>
                                    <div class='fw-semibold px-2 py-1 border-bottom ledger-email-subject'>{emailPreview.subject}</div>
                                    {/* Isolated frame: the mail carries its own light styles and must not inherit the app theme. */}
                                    <iframe
                                        class='ledger-email-body d-block w-100 border-0'
                                        title='E-mail preview'
                                        sandbox=''
                                        srcdoc={emailPreview.bodyHtml}
                                        style='height: 460px; background: #f6f6f6;'
                                    />
                                </div>
                            </div>
                        )}
                        <div class='d-flex justify-content-end mt-3'>
                            <button
                                type='button'
                                class='btn btn-outline-primary rounded-pill'
                                data-action='email'
                                disabled={!canEmail}
                                onClick={this.sendEmail}
                            >
                                {emailSending
                                    ? <span><i class='fa-solid fa-spinner fa-spin me-1' /><Text id='clubOps.ledger-email-sending'>Sending…</Text></span>
                                    : <Text id='clubOps.ledger-email-send'>Send e-mail</Text>}
                            </button>
                        </div>
                        {emailLog.length > 0 && (
                            <ul class='list-unstyled small text-body-secondary mt-2 mb-0 ledger-email-log'>
                                {emailLog.map(entry => (
                                    <li key={`${entry.date}-${entry.to}`}>
                                        <i class='fa-solid fa-check text-success me-1' />
                                        <Text
                                            id='clubOps.ledger-email-sent-log'
                                            fields={{ date: formatDate(entry.date), to: entry.to, name: entry.userName || '' }}
                                        >
                                            Sent {formatDate(entry.date)} to {entry.to} by {entry.userName || ''}
                                        </Text>
                                        {(entry.cc || []).length > 0 && (
                                            <span class='ms-1'>(cc: {entry.cc.join(', ')})</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {!!error && <div class='alert alert-danger py-2'>{error}</div>}

                <div class='d-flex justify-content-between align-items-center'>
                    <div>
                        {!isNew && !confirmDelete && (
                            <button type='button' class='btn btn-outline-danger rounded-pill' data-action='delete' onClick={() => this.setState({ confirmDelete: true })}>
                                <Text id='clubOps.ledger-delete'>Delete</Text>
                            </button>
                        )}
                        {!isNew && confirmDelete && (
                            <span>
                                <button type='button' class='btn btn-danger rounded-pill me-2' data-action='delete-confirm' onClick={this.remove}>
                                    <Text id='clubOps.ledger-delete-confirm'>Confirm delete</Text>
                                </button>
                                <button type='button' class='btn btn-link' onClick={() => this.setState({ confirmDelete: false })}>
                                    <Text id='clubOps.ledger-cancel'>Cancel</Text>
                                </button>
                            </span>
                        )}
                    </div>
                    <button type='button' class='btn btn-primary rounded-pill' data-action='save' disabled={!canSave} onClick={this.save}>
                        {isSaving && <i class='fa-solid fa-spinner fa-spin me-1' />}
                        <Text id='clubOps.ledger-save'>Save</Text>
                    </button>
                </div>
            </div>
        );
    }
}

export default ClubOpsLedgerDrawer;
